import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { familyService } from '../services/familyService'
import { Patient } from '../types/patient'
import './AddFamilyMember.css'

interface AddFamilyMemberProps {
  familyId: string
}

export function AddFamilyMember({ familyId }: AddFamilyMemberProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const queryClient = useQueryClient()

  const { data: patients, isLoading } = useQuery({
    queryKey: ['family-search-patients', familyId, searchTerm],
    queryFn: () => familyService.searchPatientsForFamily(familyId, searchTerm),
    enabled: isOpen && searchTerm.length >= 2,
  })

  const addMemberMutation = useMutation({
    mutationFn: (patientId: string) => familyService.addMember(familyId, patientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family-members', familyId] })
      queryClient.invalidateQueries({ queryKey: ['family', familyId] })
      queryClient.invalidateQueries({ queryKey: ['patients'] })
      setSearchTerm('')
      setIsOpen(false)
    },
  })

  const handleAdd = async (patient: Patient) => {
    try {
      await addMemberMutation.mutateAsync(patient.id)
    } catch (error) {
      alert('Failed to add family member')
    }
  }

  return (
    <div className="add-family-member">
      <button
        className="btn-primary btn-small"
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? 'Cancel' : '+ Add Member'}
      </button>

      {isOpen && (
        <div className="add-member-panel">
          <div className="search-section">
            <label htmlFor="patient-search">Search Patients</label>
            <input
              id="patient-search"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Type patient name or number..."
              autoFocus
            />
            {searchTerm.length > 0 && searchTerm.length < 2 && (
              <p className="search-hint">Type at least 2 characters to search</p>
            )}
          </div>

          {isLoading && <div className="loading">Searching...</div>}

          {searchTerm.length >= 2 && patients && (
            <div className="search-results">
              {patients.length === 0 ? (
                <p className="no-results">No patients found matching "{searchTerm}"</p>
              ) : (
                <ul className="patient-list">
                  {patients.map((patient) => (
                    <li key={patient.id} className="patient-item">
                      <div className="patient-info">
                        <strong>
                          {patient.firstName} {patient.lastName}
                        </strong>
                        <span className="patient-number">{patient.patientNumber}</span>
                        <span className="patient-dob">
                          DOB: {new Date(patient.dateOfBirth).toLocaleDateString()}
                        </span>
                      </div>
                      <button
                        className="btn-primary btn-small"
                        onClick={() => handleAdd(patient)}
                        disabled={addMemberMutation.isPending}
                      >
                        Add
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}



































